import { IPublicClientApplication } from '@azure/msal-browser';
import { dvList, dvUpdate } from '../api/dataverseClient';
import { ImportJob, ImportStatus } from './importService';

// ── Post-import verification checklist ─────────────────────────────────
//
// The checklist and its per-item checked-state are persisted on the
// import job's dagrun_pm_importsummaryjson MEMO field (previously unused).
// RunStep captures an ImportSummary at completion; the PM ticks items off
// on the board or from the Import tab, and the checked map is written back
// to the same field. No new schema.

export interface ImportSummary {
  rowsTotal: number;
  rowsImported: number;
  rowsFailed: number;
  unassignedTasks: number;
  collectionsCreated: number;
  dependenciesCreated: number;
  skippedDependencies: number;
  unmatchedResources: number;
  constraintsConverted: number;
  unmappedColumns: number;
  resourcePoolLinked: boolean;
  calendarRelevant: boolean;
  sourceLabel: string;
}

export interface ImportChecklistState {
  version: 1;
  summary: ImportSummary;
  checked: Record<string, boolean>;
}

export interface ChecklistItem {
  key: string;
  label: string;
  detail?: string;
  conditional: boolean;
}

export function emptySummary(): ImportSummary {
  return {
    rowsTotal: 0, rowsImported: 0, rowsFailed: 0, unassignedTasks: 0,
    collectionsCreated: 0, dependenciesCreated: 0, skippedDependencies: 0,
    unmatchedResources: 0, constraintsConverted: 0, unmappedColumns: 0,
    resourcePoolLinked: false, calendarRelevant: false, sourceLabel: '',
  };
}

/** Serialize a fresh checklist state for the import job. */
export function serializeChecklistState(summary: ImportSummary): string {
  const state: ImportChecklistState = { version: 1, summary, checked: {} };
  return JSON.stringify(state);
}

/**
 * Parse the persisted checklist state from a job. Falls back to a summary
 * derived from the job's own count fields when importsummaryjson is absent
 * (older imports made before this feature), so the checklist still renders.
 */
export function parseChecklistState(job: ImportJob): ImportChecklistState {
  const raw = job.dagrun_pm_importsummaryjson;
  if (raw) {
    try {
      const parsed = JSON.parse(raw) as Partial<ImportChecklistState>;
      if (parsed && parsed.summary) {
        return { version: 1, summary: { ...emptySummary(), ...parsed.summary }, checked: parsed.checked ?? {} };
      }
    } catch { /* fall through to derived */ }
  }
  return {
    version: 1,
    summary: {
      ...emptySummary(),
      rowsTotal: job.dagrun_pm_rowstotal ?? 0,
      rowsImported: job.dagrun_pm_rowsimported ?? 0,
      rowsFailed: job.dagrun_pm_rowsfailed ?? 0,
    },
    checked: {},
  };
}

/**
 * Standard items always shown; conditional items only when the summary
 * signal indicates they're relevant.
 */
export function buildChecklistItems(s: ImportSummary): ChecklistItem[] {
  const items: ChecklistItem[] = [
    {
      key: 'task-count', conditional: false,
      label: 'Task count matches the source',
      detail: `${s.rowsImported} of ${s.rowsTotal} rows imported${s.rowsFailed ? ` · ${s.rowsFailed} failed` : ''}.`,
    },
    {
      key: 'unassigned', conditional: false,
      label: 'Review unassigned tasks',
      detail: s.unassignedTasks > 0 ? `${s.unassignedTasks} imported task(s) have no assignee.` : 'All imported tasks have an assignee.',
    },
    {
      key: 'skipped-rows', conditional: false,
      label: 'Review skipped / failed rows',
      detail: s.rowsFailed > 0 ? `${s.rowsFailed} row(s) failed — check the import log.` : 'No rows failed.',
    },
    {
      key: 'collections', conditional: false,
      label: 'Check collection / board structure',
      detail: s.collectionsCreated > 0 ? `${s.collectionsCreated} collection(s) created from the source.` : 'No collections created — tasks landed in the default board.',
    },
    {
      key: 'date-sanity', conditional: false,
      label: 'Sanity-check project dates',
      detail: 'Confirm start/due dates and milestones look right on the timeline.',
    },
  ];

  if (s.constraintsConverted > 0) items.push({
    key: 'constraints', conditional: true,
    label: 'Verify converted scheduling constraints',
    detail: `${s.constraintsConverted} task constraint(s) were converted from the source.`,
  });
  if (s.calendarRelevant) items.push({
    key: 'work-calendar', conditional: true,
    label: 'Set up the project work calendar',
    detail: 'This source carries a schedule — confirm working days/holidays before relying on dates.',
  });
  if (s.dependenciesCreated > 0) items.push({
    key: 'modules-scheduling', conditional: true,
    label: 'Enable scheduling modules for imported data',
    detail: `${s.dependenciesCreated} dependency link(s) imported — make sure the Gantt/scheduling view is enabled.`,
  });
  if (s.resourcePoolLinked) items.push({
    key: 'resource-pool', conditional: true,
    label: 'Confirm Global Resource Pool linkage',
    detail: 'This project is pool-linked — verify imported assignees are pool members.',
  });
  if (s.unmappedColumns > 0) items.push({
    key: 'custom-fields', conditional: true,
    label: 'Review unmapped source columns as custom fields',
    detail: `${s.unmappedColumns} source column(s) were not mapped — add them as custom fields if needed.`,
  });
  if (s.unmatchedResources > 0) items.push({
    key: 'unmatched-resources', conditional: true,
    label: 'Invite / map unmatched resources',
    detail: `${s.unmatchedResources} assignee name(s) did not match an Entra user (kept as text).`,
  });
  return items;
}

/** Persist the checked map back onto the job (merged with the summary). */
export async function saveChecklistState(
  pca: IPublicClientApplication,
  jobId: string,
  state: ImportChecklistState,
): Promise<void> {
  await dvUpdate(pca, 'dagrun_pm_importjobs', jobId, {
    dagrun_pm_importsummaryjson: JSON.stringify(state),
  });
}

const JOB_FIELDS = [
  'dagrun_pm_importjobid', 'dagrun_pm_name', 'dagrun_pm_sourcetype', 'dagrun_pm_sourceidentifier',
  'dagrun_pm_status', 'dagrun_pm_rowstotal', 'dagrun_pm_rowsimported', 'dagrun_pm_rowsfailed',
  'dagrun_pm_rowsduplicate', 'dagrun_pm_importsummaryjson', '_dagrun_pm_targetprojectid_value', 'createdon',
];

/** Most recent completed/partial import job for a project (for the board panel). */
export async function getLatestImportJobForProject(
  pca: IPublicClientApplication,
  projectId: string,
): Promise<ImportJob | null> {
  const rows = await dvList<ImportJob>(pca, 'dagrun_pm_importjobs', {
    select: JOB_FIELDS,
    filter: `_dagrun_pm_targetprojectid_value eq ${projectId}`
      + ` and (dagrun_pm_status eq ${ImportStatus.Completed} or dagrun_pm_status eq ${ImportStatus.PartialSuccess})`,
    orderby: 'createdon desc',
    top: 1,
  });
  return rows[0] ?? null;
}
